import { useState, useCallback } from 'react'
import {
  CheckCircle, AlertTriangle, XCircle, RefreshCw,
  Database, Cloud, Mail, Shield, Settings, Activity, Server
} from 'lucide-react'
import { useSystemHealth } from '../hooks/useSystemHealth'

const STATUS = {
  ok:      { color: 'var(--success)',  bg: 'rgba(72,187,120,0.12)', label: 'OK',      Icon: CheckCircle },
  warn:    { color: 'var(--warning)',  bg: 'rgba(237,137,54,0.12)', label: 'Warning', Icon: AlertTriangle },
  error:   { color: 'var(--danger)',   bg: 'rgba(245,101,101,0.12)',label: 'Error',   Icon: XCircle },
  loading: { color: 'var(--text-muted)', bg: 'transparent',         label: '...',     Icon: Activity },
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.error
  const { Icon } = s
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: s.color, fontWeight: 600, fontSize: '0.8rem' }}>
      <Icon size={14} />
      {s.label}
    </span>
  )
}

function HealthCard({ title, Icon, status, children }) {
  const s = STATUS[status] || STATUS.error
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: `1px solid ${status !== 'loading' ? s.color + '44' : 'var(--border)'}`,
      borderRadius: 'var(--border-radius)',
      padding: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
          <Icon size={16} style={{ color: 'var(--text-secondary)' }} />
          {title}
        </div>
        <StatusBadge status={status} />
      </div>
      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
      </div>
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value ?? '—'}</span>
    </div>
  )
}

function ErrorDetail({ detail }) {
  if (!detail) return null
  return (
    <div style={{
      background: 'var(--bg-card-alt)',
      border: '1px solid var(--border)',
      borderRadius: 4,
      padding: '6px 8px',
      fontSize: '0.75rem',
      color: 'var(--danger)',
      wordBreak: 'break-all',
      fontFamily: 'monospace',
    }}>
      {detail}
    </div>
  )
}

export default function SystemHealth() {
  const {
    data: health,
    isLoading,
    isRefetching,
    refetch,
    error,
    dataUpdatedAt,
  } = useSystemHealth()

  const overall = health?.overall
  const overallStyle = STATUS[overall] || STATUS.loading

  const formatMs = (ms) => ms != null ? `${ms} ms` : null
  const formatHours = (h) => {
    if (h == null) return 'No data'
    if (h < 1) return `${Math.round(h * 60)} min ago`
    if (h < 24) return `${h}h ago`
    return `${Math.round(h / 24)}d ago`
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1>System Health</h1>
          {dataUpdatedAt ? (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Checked at {new Date(dataUpdatedAt).toLocaleTimeString()}
            </p>
          ) : null}
        </div>
        <button
          className="btn-secondary"
          onClick={() => refetch()}
          disabled={isLoading || isRefetching}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} style={{ animation: isRefetching ? 'spin 0.8s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {overall && (
        <div style={{
          background: overallStyle.bg,
          border: `1px solid ${overallStyle.color}44`,
          borderRadius: 'var(--border-radius)',
          padding: '0.875rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: '1.5rem',
          color: overallStyle.color,
          fontWeight: 600,
        }}>
          <overallStyle.Icon size={20} />
          {overall === 'ok'   && 'All systems operational'}
          {overall === 'warn' && 'Some components have warnings — review below'}
          {overall === 'error'&& 'One or more components are failing'}
        </div>
      )}

      {isLoading && <div className="loading">Running health checks...</div>}

      {error && (
        <div style={{ color: 'var(--danger)', padding: '1rem', background: 'var(--bg-card)', borderRadius: 'var(--border-radius)' }}>
          Failed to load health data: {error.message}
        </div>
      )}

      {health && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>

          <HealthCard title="SQL Database" Icon={Database} status={health.sql?.status}>
            <Metric label="Response time" value={formatMs(health.sql?.response_ms)} />
            <Metric label="Active customers" value={health.sql?.active_customers} />
            <Metric label="Events (24 h)" value={health.sql?.events_last_24h} />
            <Metric label="Customers active (24 h)" value={health.sql?.customers_last_24h} />
            <Metric label="Last event received" value={formatHours(health.sql?.last_event_hours_ago)} />
            <ErrorDetail detail={health.sql?.detail} />
          </HealthCard>

          <HealthCard title="Function App — Admin" Icon={Server} status={health.function_admin?.status}>
            <Metric label="func-monitoring-admin" value={null} />
            <Metric label="Response time" value={formatMs(health.function_admin?.response_ms)} />
            <ErrorDetail detail={health.function_admin?.detail} />
          </HealthCard>

          <HealthCard title="Function App — Customer" Icon={Server} status={health.function_customer?.status}>
            <Metric label="func-customer-monitoring-prod" value={null} />
            <Metric label="Response time" value={formatMs(health.function_customer?.response_ms)} />
            <ErrorDetail detail={health.function_customer?.detail} />
          </HealthCard>

          <HealthCard title="API Management" Icon={Cloud} status={health.apim?.status}>
            <Metric label="numberskills.azure-api.net" value={null} />
            <Metric label="Response time" value={formatMs(health.apim?.response_ms)} />
            <ErrorDetail detail={health.apim?.detail} />
          </HealthCard>

          <HealthCard title="Email (SMTP)" Icon={Mail} status={health.smtp?.status}>
            <Metric label="From address" value={health.smtp?.from ?? '—'} />
            <Metric label="Login time" value={formatMs(health.smtp?.response_ms)} />
            <ErrorDetail detail={health.smtp?.detail} />
          </HealthCard>

          <HealthCard title="SSL Certificate" Icon={Shield} status={health.ssl?.status}>
            <Metric label="Host" value="numberskills.azure-api.net" />
            <Metric label="Expires" value={health.ssl?.expires ?? '—'} />
            <Metric label="Days remaining" value={health.ssl?.days_remaining} />
            <ErrorDetail detail={health.ssl?.detail} />
          </HealthCard>

          <HealthCard title="Configuration" Icon={Settings} status={health.config?.status}>
            {health.config?.issues?.length === 0
              ? <Metric label="All required settings present" value="✓" />
              : health.config?.issues?.map((issue, i) => (
                  <div key={i} style={{ color: 'var(--warning)' }}>⚠ {issue}</div>
                ))
            }
            <ErrorDetail detail={health.config?.detail} />
          </HealthCard>

        </div>
      )}
    </div>
  )
}
